import citiesData from "@/data/cities.json";

import { Database } from "@/types/supabase";
import radius from "@/hooks/radius";

type CityJson = (typeof citiesData)[number];
type Event = Database["public"]["Tables"]["events"]["Row"];

export function createCitySlug(city: string): string {
  return city
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function formatCityName(city: string): string {
  return city
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getEventsWithinRadius(
  events: Event[],
  cityJson: CityJson,
  radiusInMiles: number = 100,
): Event[] {
  console.log(
    `Finding events within ${radiusInMiles} miles of ${cityJson.city}`,
  );
  console.log(`Starting with ${events.length} total events`);

  if (!events || !Array.isArray(events) || events.length === 0) {
    console.warn("No events provided to getEventsWithinRadius");
    return [];
  }

  if (!cityJson || !cityJson.latitude || !cityJson.longitude) {
    console.warn("Invalid city data for radius calculation", cityJson);
    return events; // Return all events if we can't filter
  }

  const filteredEvents = events
    .map((event) => {
      const nearestCity = findNearestCity(event, citiesData);

      // Handle missing location data
      if (!event.latitude || !event.longitude) {
        console.log(`Event ${event.id} is missing location data`);
        // For events without location, assume they're in the selected city
        return {
          ...event,
          assignedCity: cityJson.city,
          assignedCitySlug: createCitySlug(cityJson.city),
          distance: 0,
        };
      }

      const distance = radius(
        Number(cityJson.latitude ?? 0),
        Number(cityJson.longitude ?? 0),
        parseFloat(event.latitude || "0"),
        parseFloat(event.longitude || "0"),
        "M",
      );

      return {
        ...event,
        assignedCity: nearestCity?.city ?? cityJson.city,
        assignedCitySlug: nearestCity
          ? createCitySlug(nearestCity.city)
          : createCitySlug(cityJson.city),
        distance: distance,
      };
    })
    .filter((event) => event.distance <= radiusInMiles);

  console.log(`Filtered to ${filteredEvents.length} events within radius`);
  return filteredEvents;
}

export function findNearestCity(
  event: Event,
  cities: CityJson[] | null | undefined,
): CityJson | null {
  if (!Array.isArray(cities) || cities.length === 0) {
    console.warn("Invalid or empty cities data provided to findNearestCity");
    return null;
  }

  return cities.reduce<(CityJson & { distance?: number }) | null>(
    (nearest, city) => {
      if (city.latitude === null || city.longitude === null) return nearest;
      const distance = radius(
        parseFloat(city.latitude),
        parseFloat(city.longitude),
        parseFloat(event.latitude || "0"),
        parseFloat(event.longitude || "0"),
        "M",
      );
      return distance < (nearest?.distance ?? Infinity)
        ? { ...city, distance }
        : nearest;
    },
    null,
  );
}

export function normalizeCityName(cityName: string): string {
  return cityName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Allow hyphens
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
    .trim();
}
